import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PlatformInsights } from '@/components/superadmin/PlatformInsights';
import { useIsModern } from '@/modern/shared';
import { 
  Building2, 
  Users, 
  Plus, 
  Settings,
  LogIn,
  Trash2,
  Power,
  LayoutGrid,
  ShoppingCart,
  Loader2,
  Search,
  QrCode,
  LogOut,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuperAdminPanel() {
  const navigate = useNavigate();
  const modern = useIsModern();
  const { user, logout, checkAuth } = useAuth();
  
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUsersDialog, setShowUsersDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  useEffect(() => { axios.get(`${API}/public/config`).then(r => setDemoMode(!!r.data.demo_mode)).catch(() => {}); }, []);
  const [showSpacesDialog, setShowSpacesDialog] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [businessUsers, setBusinessUsers] = useState([]);
  const [businessSpaces, setBusinessSpaces] = useState([]);
  
  // Form states
  const [businessForm, setBusinessForm] = useState({
    name: '',
    business_type: 'restaurant',
    admin_email: '',
    admin_name: '',
    admin_password: ''
  });
  
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'waiter'
  });
  
  const [spaceForm, setSpaceForm] = useState({
    name: '',
    type: 'dining',
    auto_create_units: 0,
    unit_prefix: 'Mesa'
  });

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    fetchBusinesses();
  }, [user, navigate]);

  const fetchBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/admin/businesses`);
      setBusinesses(response.data);
    } catch (error) {
      console.error('Error fetching businesses:', error);
      toast.error('Error al cargar negocios');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBusiness = async () => {
    try {
      const response = await axios.post(`${API}/admin/businesses`, businessForm);
      toast.success(`Negocio "${response.data.business.name}" creado exitosamente`);
      setShowCreateDialog(false);
      setBusinessForm({
        name: '',
        business_type: 'restaurant',
        admin_email: '',
        admin_name: '',
        admin_password: ''
      });
      fetchBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear negocio');
    }
  };

  const handleSwitchToBusiness = async (businessId, businessName) => {
    try {
      await axios.post(`${API}/admin/switch-business/${businessId}`);
      // Update user context
      await checkAuth();
      toast.success(`Ahora estás configurando: ${businessName}`);
      navigate('/dashboard');
    } catch (error) {
      toast.error('Error al cambiar de negocio');
    }
  };

  const handleExitBusiness = async () => {
    try {
      await axios.post(`${API}/admin/exit-business`);
      await checkAuth();
      toast.success('Saliste del contexto del negocio');
    } catch (error) {
      toast.error('Error al salir');
    }
  };

  const handleToggleBusiness = async (businessId) => {
    try {
      const response = await axios.put(`${API}/admin/business/${businessId}/toggle`);
      setBusinesses(businesses.map(b => 
        b.business_id === businessId 
          ? { ...b, is_active: response.data.is_active }
          : b
      ));
      toast.success(response.data.is_active ? 'Negocio activado' : 'Negocio desactivado');
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleDeleteBusiness = async (businessId, businessName) => {
    if (!window.confirm(`¿Estás seguro de eliminar "${businessName}"?\n\nEsta acción eliminará TODOS los datos del negocio:\n- Usuarios\n- Espacios y mesas\n- Productos y categorías\n- Pedidos e historial\n\nEsta acción NO se puede deshacer.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/business/${businessId}`);
      setBusinesses(businesses.filter(b => b.business_id !== businessId));
      toast.success(`Negocio "${businessName}" eliminado`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar negocio');
    }
  };

  const handleShowUsers = async (business) => {
    setSelectedBusiness(business);
    try {
      const response = await axios.get(`${API}/admin/business/${business.business_id}/users`);
      setBusinessUsers(response.data);
      setShowUsersDialog(true);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    }
  };

  const handleCreateUser = async () => {
    if (!selectedBusiness) return;
    
    try {
      await axios.post(`${API}/admin/business/${selectedBusiness.business_id}/users`, userForm);
      toast.success('Usuario creado exitosamente');
      setUserForm({ name: '', email: '', password: '', role: 'waiter' });
      
      // Refresh users
      const response = await axios.get(`${API}/admin/business/${selectedBusiness.business_id}/users`);
      setBusinessUsers(response.data);
      fetchBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const handleToggleUser = async (userId) => {
    if (!selectedBusiness) return;
    
    try {
      const response = await axios.put(
        `${API}/admin/business/${selectedBusiness.business_id}/users/${userId}/toggle`
      );
      setBusinessUsers(businessUsers.map(u => 
        u.user_id === userId ? { ...u, is_active: response.data.is_active } : u
      ));
      toast.success(response.data.is_active ? 'Usuario activado' : 'Usuario desactivado');
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!selectedBusiness) return;
    
    try {
      await axios.delete(`${API}/admin/business/${selectedBusiness.business_id}/users/${userId}`);
      setBusinessUsers(businessUsers.filter(u => u.user_id !== userId));
      toast.success('Usuario eliminado');
      fetchBusinesses();
    } catch (error) {
      toast.error('Error al eliminar usuario');
    }
  };

  const handleShowSpaces = async (business) => {
    setSelectedBusiness(business);
    try {
      const response = await axios.get(`${API}/admin/business/${business.business_id}/spaces`);
      setBusinessSpaces(response.data);
      setShowSpacesDialog(true);
    } catch (error) {
      toast.error('Error al cargar espacios');
    }
  };

  const handleCreateSpace = async () => {
    if (!selectedBusiness) return;
    
    try {
      const response = await axios.post(
        `${API}/admin/business/${selectedBusiness.business_id}/spaces`, 
        spaceForm
      );
      toast.success(`Espacio "${response.data.space.name}" creado con ${response.data.space.unit_count} mesas`);
      setSpaceForm({ name: '', type: 'dining', auto_create_units: 0, unit_prefix: 'Mesa' });
      
      // Refresh spaces
      const spacesRes = await axios.get(`${API}/admin/business/${selectedBusiness.business_id}/spaces`);
      setBusinessSpaces(spacesRes.data);
      fetchBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear espacio');
    }
  };

  const handleResetDemo = async () => {
    setResetting(true);
    try {
      const response = await axios.post(`${API}/admin/reset-demo`);
      toast.success('Datos de demo reiniciados exitosamente');
      setShowResetDialog(false);
      fetchBusinesses();
      // Re-check auth to clear active_business_id
      await checkAuth();
    } catch (error) {
      toast.error('Error al reiniciar datos de demo');
    } finally {
      setResetting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const filteredBusinesses = businesses.filter(b => 
    b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.business_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'business_admin': return 'bg-purple-500';
      case 'waiter': return 'bg-blue-500';
      case 'cashier': return 'bg-green-500';
      case 'kitchen': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'business_admin': return 'Admin';
      case 'waiter': return 'Mozo';
      case 'cashier': return 'Cajero';
      case 'kitchen': return 'Cocina';
      default: return role;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {modern && demoMode && (
        <div className="flex justify-end px-4 md:px-6 pt-4">
          <Button variant="outline" className="text-orange-700 border-orange-600 hover:bg-orange-50" onClick={() => setShowResetDialog(true)} data-testid="sa-reset-demo-modern">
            <RefreshCw className="w-4 h-4 mr-2" />Reiniciar Demo
          </Button>
        </div>
      )}
      {/* Header */}
      {!modern && <header className="sticky top-0 z-50 bg-card border-b border-border px-4 md:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-red-600 flex items-center justify-center">
            <QrCode className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-lg font-semibold">Super Admin</h1>
            <p className="text-sm text-muted-foreground">
              Panel de Administración
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            className="text-orange-700 border-orange-600 hover:bg-orange-50"
            onClick={() => setShowResetDialog(true)}
            hidden={!demoMode}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reiniciar Demo
          </Button>
          {user?.active_business_id && (
            <Button variant="outline" onClick={handleExitBusiness}>
              <LogOut className="w-4 h-4 mr-2" />
              Salir del Negocio
            </Button>
          )}
          <Badge variant="destructive">Super Admin</Badge>
          <Button variant="ghost" onClick={handleLogout}>
            Cerrar Sesión
          </Button>
        </div>
      </header>}

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <PlatformInsights />
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Negocios</p>
                  <p className="text-2xl font-bold">{businesses.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Power className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Habilitados</p>
                  <p className="text-2xl font-bold">
                    {businesses.filter(b => b.is_active !== false).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Usuarios</p>
                  <p className="text-2xl font-bold">
                    {businesses.reduce((sum, b) => sum + (b.user_count || 0), 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <LayoutGrid className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Espacios</p>
                  <p className="text-2xl font-bold">
                    {businesses.reduce((sum, b) => sum + (b.space_count || 0), 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Actions */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Buscar negocio..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-business-btn">
                <Plus className="w-4 h-4 mr-2" />
                Crear Negocio
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Crear Nuevo Negocio</DialogTitle>
                <DialogDescription>
                  Configura el negocio y opcionalmente crea un usuario administrador
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Nombre del Negocio *</Label>
                  <Input
                    placeholder="Ej: Restaurant El Buen Sabor"
                    value={businessForm.name}
                    onChange={(e) => setBusinessForm({ ...businessForm, name: e.target.value })}
                    data-testid="business-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipo de Negocio</Label>
                  <Select
                    value={businessForm.business_type}
                    onValueChange={(v) => setBusinessForm({ ...businessForm, business_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="restaurant">Restaurante</SelectItem>
                      <SelectItem value="beach_club">Parador de Playa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-sm font-medium mb-3">Usuario Administrador (opcional)</p>
                  <div className="space-y-3">
                    <Input
                      placeholder="Nombre del admin"
                      value={businessForm.admin_name}
                      onChange={(e) => setBusinessForm({ ...businessForm, admin_name: e.target.value })}
                    />
                    <Input
                      type="email"
                      placeholder="Email del admin"
                      value={businessForm.admin_email}
                      onChange={(e) => setBusinessForm({ ...businessForm, admin_email: e.target.value })}
                    />
                    <Input
                      type="password"
                      placeholder="Contraseña del admin"
                      value={businessForm.admin_password}
                      onChange={(e) => setBusinessForm({ ...businessForm, admin_password: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleCreateBusiness} data-testid="submit-business-btn">
                  Crear Negocio
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Businesses List */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBusinesses.map((business) => (
            <Card key={business.business_id} className={`${business.is_active === false ? 'opacity-60' : ''}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{business.name}</CardTitle>
                    <CardDescription className="font-mono text-xs">
                      {business.business_id}
                    </CardDescription>
                  </div>
                  <Badge variant={business.is_active === false ? 'destructive' : 'secondary'}>
                    {business.is_active === false ? 'Inactivo' : 'Activo'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span>{business.user_count || 0} usuarios</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <LayoutGrid className="w-4 h-4 text-muted-foreground" />
                    <span>{business.space_count || 0} espacios</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleSwitchToBusiness(business.business_id, business.name)}
                  >
                    <LogIn className="w-4 h-4 mr-1" />
                    Entrar
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleShowUsers(business)}
                    title="Usuarios"
                  >
                    <Users className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleShowSpaces(business)}
                    title="Espacios y Mesas"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleToggleBusiness(business.business_id)}
                    title={business.is_active ? 'Desactivar' : 'Activar'}
                  >
                    <Power className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950"
                    onClick={() => handleDeleteBusiness(business.business_id, business.name)}
                    data-testid={`delete-business-${business.business_id}`}
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredBusinesses.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {searchQuery ? 'No se encontraron negocios' : 'No hay negocios creados'}
            </p>
          </div>
        )}
      </div>

      {/* Users Dialog */}
      <Dialog open={showUsersDialog} onOpenChange={setShowUsersDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Usuarios de {selectedBusiness?.name}</DialogTitle>
            <DialogDescription>
              Gestiona los usuarios del negocio
            </DialogDescription>
          </DialogHeader>
          
          {/* Create User Form */}
          <div className="p-4 bg-muted rounded-xl space-y-3">
            <p className="font-medium text-sm">Crear nuevo usuario</p>
            <div className="grid grid-cols-2 gap-3">
              <Input
                placeholder="Nombre"
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
              />
              <Input
                type="email"
                placeholder="Email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              />
              <Input
                type="password"
                placeholder="Contraseña"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
              />
              <Select
                value={userForm.role}
                onValueChange={(v) => setUserForm({ ...userForm, role: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="business_admin">Administrador</SelectItem>
                  <SelectItem value="waiter">Mozo</SelectItem>
                  <SelectItem value="cashier">Cajero</SelectItem>
                  <SelectItem value="kitchen">Cocina</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleCreateUser} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Crear Usuario
            </Button>
          </div>

          {/* Users List */}
          <div className="space-y-2">
            {businessUsers.map((user) => (
              <div 
                key={user.user_id}
                className="flex items-center justify-between p-3 rounded-xl border"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white ${getRoleBadgeColor(user.role)}`}>
                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <p className="font-medium">{user.name}</p>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={getRoleBadgeColor(user.role)}>
                    {getRoleLabel(user.role)}
                  </Badge>
                  {user.is_active === false && (
                    <Badge variant="destructive">Inactivo</Badge>
                  )}
                  <Button 
                    size="icon" 
                    variant="ghost"
                    onClick={() => handleToggleUser(user.user_id)}
                  >
                    <Power className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="icon" 
                    variant="ghost"
                    onClick={() => handleDeleteUser(user.user_id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            {businessUsers.length === 0 && (
              <p className="text-center text-muted-foreground py-4">
                No hay usuarios en este negocio
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Demo Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-5 h-5" />
              Reiniciar Datos de Demo
            </DialogTitle>
            <DialogDescription>
              Esta acción eliminará todos los datos de los negocios demo 
              (Restaurant La Costanera y Parador Sol & Mar) y los recreará 
              con datos frescos de prueba.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-sm">
              <p className="font-medium text-orange-800 mb-2">Se eliminarán:</p>
              <ul className="list-disc list-inside text-orange-700 space-y-1">
                <li>Todos los pedidos y sesiones activas</li>
                <li>Productos y categorías del menú</li>
                <li>Mesas, sombrillas y espacios</li>
                <li>Usuarios del staff</li>
              </ul>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm">
              <p className="font-medium text-green-800 mb-2">Se crearán:</p>
              <ul className="list-disc list-inside text-green-700 space-y-1">
                <li>2 negocios demo completos</li>
                <li>Staff para cada rol (admin, cocina, caja, mozo)</li>
                <li>Menús con productos de ejemplo</li>
                <li>Una sesión activa con pedido de prueba</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setShowResetDialog(false)}>
              Cancelar
            </Button>
            <Button 
              className="bg-orange-600 hover:bg-orange-700"
              onClick={handleResetDemo}
              disabled={resetting}
            >
              {resetting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Reiniciando...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reiniciar Demo
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Spaces Dialog */}
      <Dialog open={showSpacesDialog} onOpenChange={setShowSpacesDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Espacios de {selectedBusiness?.name}</DialogTitle>
            <DialogDescription>
              Gestiona los espacios y mesas del negocio
            </DialogDescription>
          </DialogHeader>
          
          {/* Create Space Form */}
          <div className="p-4 bg-muted rounded-xl space-y-3">
            <p className="font-medium text-sm">Crear nuevo espacio</p>
            <div className="grid grid-cols-2 gap-3">
              <Input
                placeholder="Nombre del espacio"
                value={spaceForm.name}
                onChange={(e) => setSpaceForm({ ...spaceForm, name: e.target.value })}
              />
              <Select
                value={spaceForm.type}
                onValueChange={(v) => setSpaceForm({ ...spaceForm, type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dining">Salón</SelectItem>
                  <SelectItem value="beach">Playa</SelectItem>
                  <SelectItem value="terrace">Terraza</SelectItem>
                  <SelectItem value="bar">Barra</SelectItem>
                  <SelectItem value="vip">VIP</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="number"
                placeholder="Crear mesas automáticamente"
                value={spaceForm.auto_create_units || ''}
                onChange={(e) => setSpaceForm({ ...spaceForm, auto_create_units: parseInt(e.target.value) || 0 })}
              />
              <Input
                placeholder="Prefijo de mesa (Ej: Mesa)"
                value={spaceForm.unit_prefix}
                onChange={(e) => setSpaceForm({ ...spaceForm, unit_prefix: e.target.value })}
              />
            </div>
            <Button 
              onClick={handleCreateSpace}
              disabled={!spaceForm.name}
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear Espacio
            </Button>
          </div>

          {/* Spaces List */}
          <div className="space-y-3 mt-4">
            {businessSpaces.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No hay espacios creados</p>
            ) : (
              businessSpaces.map((space) => (
                <div 
                  key={space.space_id} 
                  className="p-4 border rounded-lg hover:bg-muted/50"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{space.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Tipo: {space.type} | {space.unit_count || 0} mesas
                      </p>
                    </div>
                    <Badge variant={space.is_active ? 'default' : 'secondary'}>
                      {space.is_active ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </div>
                  {space.units && space.units.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {space.units.slice(0, 10).map((unit) => (
                        <Badge key={unit.unit_id} variant="outline" className="text-xs">
                          {unit.name}
                        </Badge>
                      ))}
                      {space.units.length > 10 && (
                        <Badge variant="outline" className="text-xs">
                          +{space.units.length - 10} más
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
